import {
  Combobox as HeadlessUICombobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Label,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { ReactNode, useEffect, useState } from 'react';
import { ComboboxItem } from '@employ-engage/data';

export interface ComboboxProps {
  label?: string;
  items: any[];
  additionalActions?: ReactNode[];
  selectedItem: any;
  setSelectedItem: (item: any) => void;
  mapItem: (item: any) => ComboboxItem;
  onChange?: (query: string) => void;
}

export function Combobox({
  label,
  items,
  additionalActions,
  selectedItem,
  setSelectedItem,
  mapItem,
  onChange,
}: ComboboxProps) {
  const [query, setQuery] = useState('');
  const [mappedItems, setMappedItems] = useState<ComboboxItem[]>(
    mapItem ? items?.map(mapItem) : items
  );

  const filteredItems =
    query === ''
      ? mappedItems
      : mappedItems.filter((item) => {
          return item?.name.toLowerCase().includes(query.toLowerCase());
        });

  useEffect(() => {
    setMappedItems(mapItem ? items.map(mapItem) : items);
  }, [items, mapItem]);

  return (
    <HeadlessUICombobox
      as="div"
      value={selectedItem}
      onChange={(item) => {
        if (mapItem) {
          item = items.find((i) => mapItem(i).id === item?.id);
        }
        setSelectedItem(item);
      }}
    >
      {label && (
        <Label className="block text-sm/6 font-medium text-gray-900">
          {label}
        </Label>
      )}
      <div className="relative">
        <ComboboxInput
          className="block w-full rounded-md bg-white py-1.5 pr-12 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6"
          onChange={(event) => {
            setQuery(event.target.value);
            if (onChange) {
              onChange(event.target.value);
            }
          }}
          displayValue={(item: ComboboxItem) =>
            item ? mapItem(item).name : ''
          }
        />
        <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-hidden">
          <ChevronUpDownIcon
            className="size-5 text-gray-400"
            aria-hidden="true"
          />
        </ComboboxButton>

        {filteredItems.length > 0 && (
          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 shadow-lg ring-black/5 focus:outline-hidden sm:text-sm">
            {filteredItems.map((item) => (
              <ComboboxOption
                key={item.id}
                value={item}
                className="group relative cursor-default hover:cursor-pointer hover:bg-light-primary hover:text-white active:bg-light-primary active:text-white data-focus:bg-light-primary data-focus:text-white sm:py-2 pr-9 pl-3 text-gray-900 select-none data-focus:bg-indigo-600 data-focus:text-white data-focus:outline-hidden"
              >
                <span className="block truncate group-data-selected:font-semibold">
                  {item?.name || 'unknown'}
                </span>

                <span className="absolute inset-y-0 right-0 hidden items-center pr-4 text-indigo-600 group-data-focus:text-white group-data-selected:flex">
                  <CheckIcon className="size-5" aria-hidden="true" />
                </span>
              </ComboboxOption>
            ))}
            {additionalActions?.map((action, index) => (
              <div key={index}>
                <div className="border-t border-gray-200" />
                {action}
              </div>
            ))}
          </ComboboxOptions>
        )}
      </div>
    </HeadlessUICombobox>
  );
}

export default Combobox;
